import { CreateSubcontractFormValues } from '@/modals/ContractModals/CreateSubcontractModal'
import { UpdateSubcontractFormValues } from '@/modals/ContractModals/UpdateSubcontractModal'
import { ResponseArray, ResponseObject } from '@/types/shared'
import datacentral from '@/utils/axios/datacentral'
import { Subcontract } from '../types'
import { Attachment } from '@/modules/Upload/types'

interface SubcontractParams {
    contractID: number
    contractType?: 'customer' | 'partner'
}
export const getSubcontracts = async ({
    contractID,
    contractType = 'customer',
}: SubcontractParams) => {
    const response = await datacentral.get<ResponseArray<Subcontract>>(
        `/contracts/${contractType}s/${contractID}/subcontracts`
    )

    return response?.data
}

export const genericSubcontractCode = async ({
    contractID,
    contractType = 'customer',
}: SubcontractParams) => {
    const response = await datacentral.get(
        `/contracts/${contractType}s/${contractID}/subcontracts/code`
    )

    return response?.data?.result?.subcontract_code as string
}

export const createSubcontract = async ({
    contractID,
    data,
    contractType = 'customer',
}: SubcontractParams & {
    data: Omit<CreateSubcontractFormValues, 'useServiceID'>
}) => {
    const response = await datacentral.post(
        `/contracts/${contractType}s/${contractID}/subcontracts`,
        {
            ...data,
        }
    )

    return response?.data?.result
}

export const getSubcontract = async ({
    contractID,
    subcontractID,
    contractType = 'customer',
}: SubcontractParams & {
    subcontractID: number
}) => {
    const response = await datacentral.get<ResponseObject<Subcontract>>(
        `/contracts/${contractType}s/${contractID}/subcontracts/${subcontractID}`
    )

    return response?.data?.result
}

export const updateSubcontract = async ({
    contractID,
    subcontractID,
    data,
    contractType = 'customer',
}: SubcontractParams & {
    subcontractID: number
    data: UpdateSubcontractFormValues & {
        attachments?: Attachment[]
        attachments_to_delete?: number[]
    }
}) => {
    const response = await datacentral.put(
        `/contracts/${contractType}s/${contractID}/subcontracts/${subcontractID}`,
        {
            ...data,
        }
    )

    return response?.data?.result
}

export const deleteSubcontract = async ({
    contractID,
    subcontractID,
    reason,
    contractType = 'customer',
}: SubcontractParams & {
    subcontractID: number
    reason: string
}) => {
    const response = await datacentral.delete(
        `/contracts/${contractType}s/${contractID}/subcontracts/${subcontractID}?reason=${reason}`
    )

    return response?.data?.result
}

/** One reason a service cannot join an annex, as returned by the convert/create endpoints. */
export interface ServiceConflict {
    service_id: number
    reason: string
    conflicting_contract_id?: number
    conflicting_contract_number_id?: string
    conflicting_subcontract_id?: number
    conflicting_subcontract_code?: string
}

export interface ConvertPreviewResult {
    target_contract_id: number
    target_contract_number_id: string
    source_contract_id: number
    source_contract_number_id: string
    subcontract_code_preview: string
    subcontract_status: string
    service_ids: number[]
    active_process_count: number
    blockers: string[]
    service_conflicts: ServiceConflict[]
    already_converted?: ConvertResult
    convertible: boolean
}

export interface ConvertResult {
    conversion_id: string
    target_contract_id: number
    source_contract_id: number
    subcontract_id: number
    subcontract_code: string
    moved_service_ids: number[]
    moved_process_ids: number[]
    moved_attachment_ids: number[]
    replayed: boolean
}

/** Read-only: what converting `sourceContractID` into an annex of `contractID` would do. */
export const previewConvertToSubcontract = async ({
    contractID,
    sourceContractID,
}: {
    contractID: number
    sourceContractID: number
}) => {
    const response = await datacentral.post<
        ResponseObject<ConvertPreviewResult>
    >(`/contracts/customers/${contractID}/subcontracts/from-contract/preview`, {
        source_contract_id: sourceContractID,
    })

    return response?.data?.result
}

/**
 * Converts a contract into an annex. The Idempotency-Key must be generated ONCE per user action
 * and reused on retry — the server replays the original result instead of converting twice.
 */
export const convertContractToSubcontract = async ({
    contractID,
    sourceContractID,
    reason,
    idempotencyKey,
}: {
    contractID: number
    sourceContractID: number
    reason: string
    idempotencyKey: string
}) => {
    const response = await datacentral.post<ResponseObject<ConvertResult>>(
        `/contracts/customers/${contractID}/subcontracts/from-contract`,
        { source_contract_id: sourceContractID, reason },
        { headers: { 'Idempotency-Key': idempotencyKey } }
    )

    return response?.data?.result
}
